import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import useInterval from '../../../hooks/interval'
import { Button, Checkbox, FormControlLabel, LinearProgress, makeStyles, Radio, CircularProgress, Tooltip } from '@material-ui/core'
import { observer } from 'mobx-react-lite';
import { useDropzone } from 'react-dropzone'
import {
    DataGridPro,
    GridColDef,
    GridFooterContainer,
    GridPagination,
    GridRowId,
    GridValueFormatterParams,
    GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import type { GridState } from '@mui/x-data-grid-pro';
import StepConfirmation, { ConfirmationDialogProps, defaultConfirmationDialogProps } from '../../controls/StepConfirmation';
import { Stack, Typography, Chip, Box } from '@mui/material';
import { activeIngestStatus, PENDING_DELETION_STATUS, unselectableStatus } from '../types';
import { IngestFile, IngestPayload, PrepareDocumentsResponse } from '../models';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CopyTooltip from '../components/CopyTooltip';
import { CopilotStore } from '../CopilotStore';
import { CSVUploadButton, CSVSchema } from '../../controls/CSVUploadButton';

const useStyles = makeStyles((theme) => ({
    pageRoot: {
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        boxSizing: 'border-box',
    },
    toolbarRow: {
        marginTop: theme.spacing(1.25),
        display: 'flex',
        gap: theme.spacing(1.25),
        alignItems: 'center',
        flexShrink: 0,
    },
    separator: {
        width: '100%',
        minHeight: 75,
        backgroundColor: '#ffffff',
        borderStyle: 'dashed',
        border: '1px dashed #858F9F',
        boxSizing: 'border-box',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#858F9F'
    },
    root: {
        "& .MuiTypography-root": {
            minWidth: '100%',
        }
    },
    textbtn: {
        textTransform: 'none',
        backgroundColor: 'transparent',
        fontSize: "inherit",
        fontWeight: 'inherit',
        padding: 0,
        '&:hover': {
            backgroundColor: 'transparent'
        },
        lineHeight: '19px'
    },
    loadingIndicator: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        zIndex: 1
    },
    documentsGridOuter: {
        marginTop: theme.spacing(1.25),
        flex: 1,
        minHeight: 320,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    dataGrid: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        backgroundColor: '#ffffff',
        '& .MuiDataGrid-main': {
            flex: 1,
            minHeight: 0,
        },
        '& .MuiDataGrid-virtualScroller': {
            minHeight: 0,
        },
        '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #e0e0e0',
        },
        '& .MuiDataGrid-columnHeader': {
            padding: '8px 4px',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.875rem',
        },
        '& .MuiDataGrid-row': {
            backgroundColor: '#ffffff',
        },
        '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f8f9fa',
        },
        '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: '#e3f2fd',
        },
        '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: '#e3f2fd',
        },
        '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #e0e0e0',
        },
        '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root.Mui-disabled': {
            color: '#bdbdbd',
        }
    },
    statusCellFill: {
        padding: '0 !important',
        alignItems: 'stretch !important',
        '& > *': {
            flex: 1,
            minWidth: 0,
            alignSelf: 'stretch',
        }
    },
}));

type CopilotKnowledgeArticlesProps = {
    store: CopilotStore
    uploadToPresignedUrl: Function
}

export const CopilotKnowledgeArticles = observer(({store, uploadToPresignedUrl}: CopilotKnowledgeArticlesProps) => {
    const classes = useStyles()
    const {
        user_email,
        selectedCustomer: customer,
        tenantConfig,
        ingestFiles: files, 
        filesLoading, 
        postSyncStorage,
        refreshFiles, 
        addIngestFiles,
        deleteIngestFiles,
        setSelectedFiles,
        selectedFilesIds,
        selectedFiles,
        copilotIngest,
        fetchKaUploadUrlByFileName,
        refreshFileStatus,
        tagConfigs,
        fileById,
        filesByDocumentName,
        fileStatusCounts,
    } = store

    const [inProgress, setInProgress] = useState<boolean>(false)
    const [uploadProgress, setUploadProgress] = useState<Array<{ isUploading: boolean, uploadedFile?: string, position?: number, total?: number }> | undefined>(undefined)
    const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps | undefined>(undefined)
    const [skipCache, setSkipCache] = useState(false)
    const ingestTypeRef = useRef<"selected" | "new" | "new&failed" | undefined>(undefined)
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
    const [loadingProgress, setLoadingProgress] = useState<{ loaded: number, total?: number }>({ loaded: 0 })
    const [deleteProgress, setDeleteProgress] = useState<{ deleting: number, total: number } | undefined>(undefined)
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })
    const [syncTaskId, setSyncTaskId] = useState<string | undefined>(undefined)
    const [csvLoading, setCsvLoading] = useState<boolean>(false)
    const [docsInOfflinePrep, setDocsInOfflinePrep] = useState<{ document_id: string, progress: string }[]>([])
    // Ref to track visible rows for select-all fix (using ref to avoid re-renders)
    const visibleRowIdsRef = useRef<string[]>([])
    const docsInOfflinePrepById = useMemo(() => {
        const lookup = new Map<string, { document_id: string, progress: string }>()
        docsInOfflinePrep.forEach(doc => lookup.set(doc.document_id, doc))
        return lookup
    }, [docsInOfflinePrep])
    const { pending, processing, failed, done, pendingDeletion } = fileStatusCounts

    // CSV upload schema for document selection
    const csvSchema: CSVSchema = {
        fields: [
            {
                name: 'documents',
                required: true,
                type: 'string',
                validator: (value: string) => {
                    if (!value || value.trim() === '') {
                        return 'Documents cannot be empty';
                    }
                    return true;
                }
            }
        ],
        allowExtraFields: false,
        minRows: 1
    };

    // Track when files are refreshing vs. initial load
    useEffect(() => {
        if (filesLoading && files.length > 0) {
            setIsRefreshing(true)
        } else if (!filesLoading) {
            // Delay the end of refreshing indicator to ensure UI doesn't disappear too early
            setTimeout(() => {
                setIsRefreshing(false)
                setLoadingProgress({ loaded: 0 }) // Reset progress when done
            }, 200)
        }
    }, [filesLoading, files.length])

    // Track loading progress
    useEffect(() => {
        if (filesLoading) {
            setLoadingProgress(prev => ({ ...prev, loaded: files.length }))
        }
    }, [files.length, filesLoading])

    useEffect(() => {
        setInProgress(uploadProgress !== undefined)
    }, [uploadProgress])

    useEffect(() => {
        if (ingestTypeRef.current) {
            setDialogProps(getIngestConfirmationDialog(ingestTypeRef.current))
        }
    }, [skipCache])

    useEffect(() => {
        setSyncTaskId(undefined)
        if (customer?.name) {
            store.backend!.getSyncStorageTask(customer.name).then((res: any) => {
                setSyncTaskId(res.data.task_id)
            })
            // prepare offline: store.backend!.getOfflinePrepStatus(customer.name).then((res: PrepareDocumentsResponse) => { setDocsInOfflinePrep(res.documents) })
        }
    }, [customer?.name])

    useInterval(() => {
        if (customer?.name) {
            store.backend!.getSyncStorageTask(customer.name).then((res: any) => {
                setSyncTaskId(res.data.task_id)
            }).catch((error: any) => {
                console.error('Failed to get sync storage task:', error);
                setSyncTaskId(undefined)
            })
        }
    }, 2000, !!syncTaskId && !!customer?.name)


   const normalize = (value: number, uploadProgress: { isUploading: boolean, uploadedFile?: string, position?: number, total?: number }) =>
    uploadProgress && uploadProgress.total ? (value - 0) * 100 / (uploadProgress.total - 0) : 0;

   const filteredUploadProgress = uploadProgress && uploadProgress.filter(up => !up.position || normalize(up.position, up) < 100)

    const disableAll = () => !customer?.name || filesLoading || inProgress || csvLoading

    const MyDropzone = () => {
        const onDropAccepted = useCallback(acceptedFiles => {
            handleProcess(acceptedFiles)
        }, [])
        const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDropAccepted, accept: ['application/pdf', 'video/mp4', 'text/csv'], disabled: disableAll() })

        return (
            <div className={classes.separator}
                {...getRootProps()}>
                <input {...getInputProps()} />
                {
                    isDragActive ?
                        <p>Drop The KA File Here ...</p> :
                        <p>Drag & Drop your KA File or Click To Select (PDF, MP4, CSV)</p>
                }
            </div>
        )
    }

    const handleProcess = async (uploadFiles: Array<File>) => {
        const duplicateFiles = uploadFiles.filter((file) => filesByDocumentName.has(file.name));

        if (duplicateFiles.length > 0) {
            setDialogProps({
                ...defaultConfirmationDialogProps,
                title: 'Duplicate Files Detected',
                content: (
                    <div>
                        <Typography variant='body1'>
                            The following file/s already exist in the system:
                        </Typography>
                        <ul>
                            {duplicateFiles.map((file) => (
                                <li key={file.name}>{file.name}</li>
                            ))}
                        </ul>
                        <Typography variant='body1'>
                            Do you want to overwrite the existing file/s?
                        </Typography>
                    </div>
                ),
                onYes: () => {
                    setDialogProps(undefined); 
                    handleUpload(uploadFiles)
                },
                yesLabel: 'Yes, Overwrite',
                onNo: () => {
                    setDialogProps(undefined);
                    const newUploadFiles = uploadFiles.filter((file) => !filesByDocumentName.has(file.name));
                    if (newUploadFiles.length > 0) {
                        handleUpload(newUploadFiles);
                    }
                },
                noLabel: 'Skip existing files',
                closeLabel: 'Cancel Upload',
            });
        } else {
            handleUpload(uploadFiles);
        } 
    }

    const handleUpload = async (files: Array<File>) => {
        const newUploadedFiles: Array<{ isUploading: boolean, uploadedFile?: string, position?: number, total?: number }> =
            files.map(f => ({ uploadedFile: f.name, isUploading: true, position: undefined, total: undefined }))
        setUploadProgress(newUploadedFiles)

        const fileNames = files.map(f => f.name)
        const presignedDataUrls = await fetchKaUploadUrlByFileName(fileNames)
        const uploads: Promise<any>[] = []
        presignedDataUrls.forEach((presignedData: any) => {
            let file: File | undefined;
            if (presignedData.url.includes('blob')){
                file = files.find(f => presignedData.object_name.toString().endsWith(f.name))
            } else {
                file = files.find(f => presignedData.fields['key'].toString().endsWith(f.name))
            }
            if (file !== undefined){
                uploads.push(uploadToPresignedUrl(file!, presignedData, (position: number, total: number) => {
                    const foundIndex = newUploadedFiles.findIndex(up => up.uploadedFile === file!.name)
                    if (foundIndex >= 0) {
                        const copy = [...newUploadedFiles]
                        copy[foundIndex].position = position
                        copy[foundIndex].total = total
                        setUploadProgress(copy)
                    }
                }))
            }
        });
        await Promise.all(uploads)
        await addIngestFiles(fileNames)
        
        // Clear upload progress - no need to refresh files since we added them efficiently
        setUploadProgress(undefined)
    }

    const columns: GridColDef[] = useMemo(() => [
        {
            field: 'id',
            headerName: 'Id',
            flex: 1,
            editable: false,
            renderCell: (params: any) => <CopyTooltip text={params.value}/>
        },
        {
            field: 'document_name',
            headerName: 'Name',
            flex: 4,
            editable: false,
            renderCell: (params: any) => <CopyTooltip text={params.value}/>
        },
        ...tagConfigs
            .slice()
            .filter(config => !String(config.tag_index).includes("temp"))
            .sort((a, b) => Number(a.tag_index) - Number(b.tag_index))
            .map(config => ({
                field: `tags_${config.tag_index}`,
                headerName: config.tag_label || `Tag Index ${config.tag_index}`,
                flex: 2,
                editable: false,
                valueGetter: (params: GridValueGetterParams) => {
                    const fileTags = params.row.tags || []
                    const indexTags = fileTags.filter((tag: any) => tag.tag_index === config.tag_index)
                    return indexTags.map((tag: any) => tag.value).join(', ')
                },
                renderCell: (params: any) => <CopyTooltip text={params.value || ''}/>
            })),
        {
            field: 'created_at',
            headerName: 'Created At',
            type: 'date',
            flex: 1,
            editable: false,
            valueFormatter: (params: GridValueFormatterParams) => {
                const v = params.value
                if (v == null || v === '') return ''
                const d = v instanceof Date ? v : new Date(typeof v === 'string' || typeof v === 'number' ? Date.parse(String(v)) : NaN)
                return Number.isNaN(d.getTime()) ? '' : d.toLocaleString()
            },
            renderCell: (params: any) => <CopyTooltip text={String(params.formattedValue ?? '')}/>
        },
        {
            field: 'created_by',
            headerName: 'Created By',
            flex: 1,
            editable: false,
            renderCell: (params: any) => <CopyTooltip text={params.value}/>
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            editable: false,
            align: 'center',
            cellClassName: classes.statusCellFill,
            renderCell: (params: any) => {
                const status = params.value
                let backgroundColor = '#000000'
                if (status === 'Uploaded') backgroundColor = '#D3D3D3'
                else if (activeIngestStatus.includes(status)) backgroundColor = '#ADD8E6'
                else if (status === 'Failed') backgroundColor = '#ffcccc'
                else if (status === 'Done') backgroundColor = '#d4edda'
                else if (status === PENDING_DELETION_STATUS) backgroundColor = '#ffe0b2'
                return (
                    <div
                        style={{
                            backgroundColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            padding: '0 8px',
                        }}
                    >
                        {status}
                    </div>
                )
            }
        },
        {
            field: 'offline_ready',
            headerName: '',
            width: 76,
            editable: false,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => <WifiOffIcon style={{ color: 'rgba(0, 0, 0, 0.54)' }} />,
            renderCell: (params: any) => {
                const offlineDoc = docsInOfflinePrepById.get(params.row.id)
                if (offlineDoc) {
                    const progress = offlineDoc.progress
                    if (progress) {
                       return <div style={{ color: '#2196f3' }}>{progress}</div>
                    }
                    return <CircularProgress size={20} style={{ color: '#2196f3' }} />
                } else if (params.value) {
                    return <CheckIcon style={{ color: '#4caf50' }} />
                } else {
                    return <RemoveIcon style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                }
            }
        },
    ], [classes.statusCellFill, docsInOfflinePrepById, tagConfigs]);

    // Track visible rows when grid state changes - MUI SELECT ALL BUG FIX (v6 state shape)
    const handleGridStateChange = (state: GridState) => {
        if (state.rows?.dataRowIds && state.visibleRowsLookup) {
            visibleRowIdsRef.current = state.rows.dataRowIds
                .filter((id: GridRowId) => state.visibleRowsLookup[id] !== false)
                .map((id: GridRowId) => id as string)
        }
    }

    const setGridSelectedFile = (selected: GridRowId[]) => {
        // Filter selection to only include visible rows - MUI SELECT ALL BUG FIX
        const visibleIds = visibleRowIdsRef.current
        const visibleSet = new Set(visibleIds.length > 0 ? visibleIds : Array.from(fileById.keys()))
        const filteredSelection = (selected as string[]).filter(id => visibleSet.has(id))
        
        const selectedFiles = filteredSelection
            .map(id => fileById.get(id))
            .filter((file): file is IngestFile => file !== undefined)
        setSelectedFiles(selectedFiles)
    }

    const handleCSVSelection = (csvData: any[], errors: string[]) => {
        if (errors.length > 0) {
            console.error('CSV validation errors:', errors);
            setInProgress(false);
            return;
        }

        setSelectedFiles([])

        const identifiers = csvData.map(row => row.documents?.trim()).filter(Boolean);
        if (identifiers.length === 0) {
            setInProgress(false);
            setDialogProps({
                ...defaultConfirmationDialogProps,
                title: 'No Valid Documents',
                content: (
                    <Typography variant='body1'>
                        No valid documents found in the CSV file.
                    </Typography>
                ) as JSX.Element,
                onYes: () => setDialogProps(undefined),
                yesLabel: 'OK'
            });
            return;
        }

        // Find matching files by ID or filename
        const matchedFiles: IngestFile[] = [];
        const duplicateFilenames: string[] = [];
        const notFoundDocuments: string[] = [];
        const unselectableDocuments: string[] = [];
        const duplicateFilenameSet = new Set<string>();
        const notFoundDocumentSet = new Set<string>();
        const unselectableDocumentSet = new Set<string>();

        identifiers.forEach(identifier => {
            // First try to match by ID
            const matchedById = fileById.get(identifier);
            if (matchedById) {
                // Skip documents that can't be selected (processing, queued or pending deletion)
                if (unselectableStatus.includes(matchedById.status)) {
                    if (!unselectableDocumentSet.has(identifier)) {
                        unselectableDocumentSet.add(identifier);
                        unselectableDocuments.push(identifier);
                    }
                } else {
                    matchedFiles.push(matchedById);
                }
                return;
            }

            // Then try to match by filename
            const matchedByFilename = filesByDocumentName.get(identifier) ?? [];
            if (matchedByFilename.length > 0) {
                if (matchedByFilename.length > 1) {
                    // Check if we already recorded this duplicate filename
                    if (!duplicateFilenameSet.has(identifier)) {
                        duplicateFilenameSet.add(identifier);
                        duplicateFilenames.push(identifier);
                    }
                }
                
                // Filter out documents that can't be selected
                const selectableFiles = matchedByFilename.filter(f => !unselectableStatus.includes(f.status));
                const unselectableFiles = matchedByFilename.filter(f => unselectableStatus.includes(f.status));
                
                if (unselectableFiles.length > 0 && !unselectableDocumentSet.has(identifier)) {
                    unselectableDocumentSet.add(identifier);
                    unselectableDocuments.push(identifier);
                }
                
                matchedFiles.push(...selectableFiles);
            } else {
                // No match found
                if (!notFoundDocumentSet.has(identifier)) {
                    notFoundDocumentSet.add(identifier);
                    notFoundDocuments.push(identifier);
                }
            }
        });

        // Remove duplicates from matched files (in case same file was matched multiple times)
        const uniqueMatchedFiles: IngestFile[] = [];
        const matchedFileIds = new Set<string>();
        matchedFiles.forEach(file => {
            if (!matchedFileIds.has(file.id)) {
                matchedFileIds.add(file.id);
                uniqueMatchedFiles.push(file);
            }
        });

        // Show warnings and confirmations
        let warningContent: React.ReactNode;
        
        if (duplicateFilenames.length > 0 || notFoundDocuments.length > 0 || unselectableDocuments.length > 0) {
            warningContent = (
                <div>
                    {duplicateFilenames.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <Typography variant='subtitle2' style={{ color: '#ff9800', marginBottom: 8 }}>
                                ⚠️ Multiple documents found for these filenames:
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {duplicateFilenames.map(filename => (
                                    <li key={filename}>{filename}</li>
                                ))}
                            </ul>
                            <Typography variant='body2' style={{ marginTop: 8 }}>
                                All selectable documents with these filenames will be selected.
                            </Typography>
                        </div>
                    )}
                    
                    {unselectableDocuments.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <Typography variant='subtitle2' style={{ color: '#2196f3', marginBottom: 8 }}>
                                ℹ️ Documents currently processing, queued or pending deletion cannot be selected:
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {unselectableDocuments.map(identifier => (
                                    <li key={identifier}>{identifier}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    {notFoundDocuments.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <Typography variant='subtitle2' style={{ color: '#f44336', marginBottom: 8 }}>
                                ❌ Documents not found:
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {notFoundDocuments.map(identifier => (
                                    <li key={identifier}>{identifier}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    <Typography variant='body1' style={{ marginTop: 16 }}>
                        {uniqueMatchedFiles.length > 0 
                            ? `${uniqueMatchedFiles.length} documents will be selected. Do you want to proceed?`
                            : 'No documents will be selected.'
                        }
                    </Typography>
                </div>
            );
        } else {
            warningContent = (
                <Typography variant='body1'>
                    {uniqueMatchedFiles.length} documents will be selected. Do you want to proceed?
                </Typography>
            );
        }

        setInProgress(false);
        
        if (uniqueMatchedFiles.length > 0) {
            setDialogProps({
                ...defaultConfirmationDialogProps,
                title: 'CSV Selection Results',
                content: warningContent as JSX.Element,
                onYes: () => {
                    setSelectedFiles(uniqueMatchedFiles);
                    setDialogProps(undefined);
                },
                yesLabel: 'Select Documents',
                closeLabel: 'Cancel'
            });
        } else {
            setDialogProps({
                ...defaultConfirmationDialogProps,
                title: 'No Documents Selected',
                content: warningContent as JSX.Element,
                onYes: () => setDialogProps(undefined),
                yesLabel: 'OK'
            });
        }
    };

    const onRefreshStatus = async () => {
        // prepare offline: store.backend!.getOfflinePrepStatus(customer!.name).then((res: PrepareDocumentsResponse) => { setDocsInOfflinePrep(res.documents) })
        await refreshFileStatus()
    }

    const MAX_DELETE_BATCH_SIZE = 300

    const onDelete = async () => {
        setInProgress(true)
        setDialogProps(undefined)
        
        const totalFiles = selectedFiles.length
        let deleting = 0
        let failed = 0
        setDeleteProgress({ deleting: deleting, total: totalFiles })

        const batchCount = Math.ceil(totalFiles / MAX_DELETE_BATCH_SIZE)
        for (let i = 0; i < batchCount; i++) {
            const batch = selectedFiles.slice(i * MAX_DELETE_BATCH_SIZE, (i + 1) * MAX_DELETE_BATCH_SIZE)
            deleting += batch.length
            setDeleteProgress({ deleting: deleting, total: totalFiles })
            try {
                const response = await deleteIngestFiles(batch)
                const deleted = response.data
                failed += batch.length - deleted
            } catch (error) {
                console.error('Failed to delete ingest files batch:', error);
                failed += batch.length
            }
        }

        if (failed === totalFiles) {
            setDialogProps({
                ...defaultConfirmationDialogProps,
                title: 'Delete Failed',
                content: (
                    <Stack direction={'column'} spacing={1}>
                        <Typography variant='body1'>
                            Failed to delete the selected documents.
                        </Typography>
                        <Typography variant='body1'>
                            Please try again or contact support if the problem persists.
                        </Typography>
                    </Stack>
                ),
                onYes: () => setDialogProps(undefined),
                yesLabel: 'OK'
            })
        }
        else {
            setDialogProps({...defaultConfirmationDialogProps,
                title: 'Deletion Started',
                content: (
                    <Stack direction={'column'} spacing={1}>
                        <Typography variant='body1'>
                            {totalFiles-failed} documents were marked for deletion.
                        </Typography>
                        <Typography variant='body1'>
                            They are excluded from search right away, but the rows and files stay visible until the deletion task finishes. Please allow some time for it to complete.
                        </Typography>
                        {failed > 0 && 
                            <Stack direction={'column'} spacing={1} style={{backgroundColor: '#fff3e0', padding: 10, borderRadius: 5}}>
                                <Typography variant='body1'>
                                    Failed to start deletion for {failed} documents.
                                </Typography>
                                <Typography variant='body1'>
                                    Please try again or contact support if the problem persists.
                                </Typography>
                            </Stack>
                        }
                    </Stack>
                ),
                onYes: () => setDialogProps(undefined),
                yesLabel: 'OK'
            })
        }

        setInProgress(false)
        setDeleteProgress(undefined)
    }

    const getDeleteConfirmationDialog = () => {
        const fileCount = selectedFiles.length
        const willBeBatched = fileCount > MAX_DELETE_BATCH_SIZE
        const batchCount = willBeBatched ? Math.ceil(fileCount / MAX_DELETE_BATCH_SIZE) : 1
        
        const saveConfirmProps: ConfirmationDialogProps = {
            ...defaultConfirmationDialogProps,
            title: 'Delete Files & Clear Cache?',
            content: <>
                <div style={{ display: 'flex', marginBottom: 15, marginTop: 15, flexDirection: 'column' }} >
                    <Typography variant='subtitle1'>
                        This action will remove {fileCount.toLocaleString()} selected files and clear all cached responses generated using them.
                    </Typography>
                    {willBeBatched && (
                        <Typography variant='subtitle1' style={{marginTop: 10, color: '#ff9800'}}>
                            Due to API limits, this operation will be processed in {batchCount} batches of up to {MAX_DELETE_BATCH_SIZE} files each.
                        </Typography>
                    )}
                </div>
                <Typography variant='body1' style={{marginTop: 15}}>
                    {willBeBatched ? 'This may take some time to complete. Are you sure you want to proceed?' : 'Are you sure you want to proceed?'}
                </Typography>
            </>,
            onYes: onDelete,
            yesLabel: 'Yes & Delete',
            closeLabel: 'No',
        }

        return saveConfirmProps
    }

    const onSync = async () => {
        setDialogProps(undefined)
        if (!customer?.name) return
        postSyncStorage(customer.name).then((res: any) => {
            setSyncTaskId(res.data.task_id)
        }).catch((err: any) => {
            console.error('Failed to sync file storage:', err);
            setDialogProps({
                ...defaultConfirmationDialogProps,
                title: 'Sync Failed',
                content: (
                    <div>
                        <Typography variant='body1'>
                            Failed to sync file storage: {err.message}
                        </Typography>
                    </div>
                ),
                onYes: () => setDialogProps(undefined),
                yesLabel: 'OK'
            })
        })
    }

    const getSyncConfirmationDialog = () => {
        const saveConfirmProps: ConfirmationDialogProps = {
            ...defaultConfirmationDialogProps,
            title: 'Sync File Storage?',
            content: <>
                <div style={{ display: 'flex', marginBottom: 15, marginTop: 15, flexDirection: 'column'}} >
                    <Typography variant='subtitle1'>
                        This action will sync the tenant's file storage:
                    </Typography>
                    <Typography variant='subtitle1'>
                        • It will add to the database new files found in storage.
                    </Typography>
                    <Typography variant='subtitle1'>
                        • It will NOT remove any documents not found in file storage.
                    </Typography>
                </div>
                <Typography variant='body1' style={{marginTop: 15}}>
                    Would you like to proceed?
                </Typography>
            </>,
            onYes: onSync,
            yesLabel: 'Yes',
            closeLabel: 'No',
        }

        return saveConfirmProps
    }


    const onIngest = async (rerun_failed: boolean, document_ids?: Array<string>, skip_cache?: boolean) => {
        setDialogProps(undefined)
        ingestTypeRef.current = undefined
        setSkipCache(false)
        setInProgress(true)
        try {
            const ingest_payload: IngestPayload = {
                document_ids: document_ids,
                rerun_failed: rerun_failed,
                requested_by_email: user_email,
                skip_cache: skip_cache,
            }
            await copilotIngest(customer!.name, ingest_payload)
            await onRefreshStatus()
        } catch (error) {
            console.error('Ingest failed:', error)
        } finally {
            setInProgress(false)
        }
    }

    const getIngestConfirmationDialog = (ingest_type: "selected" | "new" | "new&failed") => {
        const message = ingest_type === "selected" ? "the selected documents" :
        ingest_type === "new" ? "all documents with status: Uploaded" :
        "all documents with status: Uploaded or Failed"

        const saveConfirmProps: ConfirmationDialogProps = {
            ...defaultConfirmationDialogProps,
            title: 'Ingest Documents?',
            content: <>
                <div style={{ display: 'flex', marginBottom: 15, marginTop: 15, flexDirection: 'column'}} >
                    <Typography variant='subtitle1'>
                        This action will ingest {message}.
                    </Typography>
                    <Typography variant='subtitle1'>
                        • Previous ingest data and vectors for these documents will be removed prior to ingesting.
                    </Typography>
                </div>
                <Typography variant='body1' style={{marginTop: 15}}>
                    Would you like to proceed?
                </Typography>
            </>,
            actionsPrefix: <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Checkbox
                    checked={skipCache}
                    onChange={(e) => setSkipCache(e.target.checked)}
                    color="primary"
                    size="small"
                    style={{ padding: 4 }}
                />
                <Typography variant='body2'>Skip cache</Typography>
                <Tooltip title="Only applies to MSR ingests. Other ingest methods ignore this option.">
                    <InfoOutlined fontSize="small" color="action" />
                </Tooltip>
            </div>,
            onYes: ingest_type === "selected" ? () => onIngest(false, selectedFilesIds, skipCache) :
                   ingest_type === "new" ? () => onIngest(false, undefined, skipCache) :
                   () => onIngest(true, undefined, skipCache),
            yesLabel: 'Yes',
            closeLabel: 'No',
        }

        return saveConfirmProps
    }

    const onPrepareOffline = async (documentIds: string[]) => {
        setDialogProps(undefined)
        setInProgress(true)
        try {
            const prepStatusResponse = await store.backend!.getOfflinePrepStatus(customer!.name)
            setDocsInOfflinePrep(prepStatusResponse.documents)

            const filteredDocIds = documentIds.filter(id => !prepStatusResponse.documents.find(doc => doc.document_id === id))
            const alreadyInProgress = documentIds.filter(id => prepStatusResponse.documents.find(doc => doc.document_id === id))

            if (filteredDocIds.length === 0) {
                setDialogProps({
                    ...defaultConfirmationDialogProps,
                    title: 'No Documents to Prepare',
                    content: (
                        <Typography variant='body1'>
                            All selected documents are already being prepared for offline use.
                        </Typography>
                    ),
                    onYes: () => setDialogProps(undefined),
                    yesLabel: 'OK'
                })
                setInProgress(false)
                return
            }

            const response = await store.backend!.prepareOfflineData(customer!.name, filteredDocIds)
            setDocsInOfflinePrep(response.documents)

            const messageContent = alreadyInProgress.length > 0 ? (
                <div>
                    <Typography variant='body1' style={{ marginBottom: 15 }}>
                        Offline data preparation has been initiated for {filteredDocIds.length} document{filteredDocIds.length > 1 ? 's' : ''}.
                    </Typography>
                    <Typography variant='body2' style={{ color: '#ff9800' }}>
                        ⚠️ {alreadyInProgress.length} document{alreadyInProgress.length > 1 ? 's were' : ' was'} skipped (already in progress).
                    </Typography>
                </div>
            ) : (
                <Typography variant='body1'>
                    Offline data preparation has been initiated for {filteredDocIds.length} document{filteredDocIds.length > 1 ? 's' : ''}.
                </Typography>
            )

            setDialogProps({
                ...defaultConfirmationDialogProps,
                title: 'Offline Preparation Initiated',
                content: messageContent,
                onYes: () => setDialogProps(undefined),
                yesLabel: 'OK'
            })
            await onRefreshStatus()
        } catch (error: any) {
            console.error('Failed to prepare offline data:', error);
            setDialogProps({
                ...defaultConfirmationDialogProps,
                title: 'Preparation Failed',
                content: (
                    <Typography variant='body1'>
                        Failed to prepare offline data: {error.message || 'Unknown error'}
                    </Typography>
                ),
                onYes: () => setDialogProps(undefined),
                yesLabel: 'OK'
            })
        } finally {
            setInProgress(false)
        }
    }

    const getPrepareOfflineConfirmationDialog = () => {
        const alreadyPrepared = selectedFiles.filter(f => f.offline_ready)
        const notPrepared = selectedFiles.filter(f => !f.offline_ready)

        if (alreadyPrepared.length === 0) {
            const confirmProps: ConfirmationDialogProps = {
                ...defaultConfirmationDialogProps,
                title: 'Prepare Offline Data?',
                content: (
                    <div>
                        <Typography variant='body1' style={{ marginBottom: 15 }}>
                            This will prepare {selectedFiles.length} document{selectedFiles.length > 1 ? 's' : ''} for offline use.
                        </Typography>
                        <Typography variant='body1'>
                            Would you like to proceed?
                        </Typography>
                    </div>
                ),
                onYes: () => onPrepareOffline(selectedFilesIds),
                yesLabel: 'Yes',
                closeLabel: 'No',
            }
            return confirmProps
        } else {
            const confirmProps: ConfirmationDialogProps = {
                ...defaultConfirmationDialogProps,
                title: 'Some Documents Already Prepared',
                content: (
                    <div>
                        <Typography variant='body1' style={{ marginBottom: 15, color: '#ff9800' }}>
                            ⚠️ {alreadyPrepared.length} of the {selectedFiles.length} selected document{selectedFiles.length > 1 ? 's are' : ' is'} already prepared for offline use.
                        </Typography>
                    </div>
                ),
                onYes: () => onPrepareOffline(selectedFilesIds),
                yesLabel: `Prepare All (${selectedFiles.length})`,
                onNo: () => onPrepareOffline(notPrepared.map(f => f.id)),
                noLabel: `Only Unprepared (${notPrepared.length})`,
                closeLabel: 'Cancel',
            }
            return confirmProps
        }
    }

    const handleIngestOnClick = (type: "selected" | "new" | "new&failed") => {
        ingestTypeRef.current = type;
        setDialogProps(getIngestConfirmationDialog(type));
    }

    const handleCancelIngestOnClick = () => {
        setDialogProps(undefined);
        ingestTypeRef.current = undefined;
        setSkipCache(false);
    }

    return (
        <div className={classes.pageRoot}>
            {/* Subtle loading indicator at top */}
            {filesLoading && (
                <div className={classes.loadingIndicator}>
                    <LinearProgress variant="indeterminate" />
                </div>
            )}
            
            {<MyDropzone />}
            <div className={classes.toolbarRow}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
                <Stack direction={'row'} spacing={2} alignItems="center">
                    <CSVUploadButton
                        schema={csvSchema}
                        onLoadingStart={() => setCsvLoading(true)}
                        onLoadingEnd={() => setCsvLoading(false)}
                        onDataLoaded={handleCSVSelection}
                        tooltipText="Select documents from CSV (ID or filename)"
                        disabled={disableAll() || files.length === 0}
                        size="small"
                        style={{marginLeft: 12}}
                    />
                    <Button variant='outlined' onClick={() => setDialogProps(getSyncConfirmationDialog())} 
                    disabled={customer === undefined || Boolean(syncTaskId)}
                    startIcon={syncTaskId ? <CircularProgress size={16} /> : undefined}
                    >
                        {syncTaskId ? 'Syncing Storage' : 'Sync Storage'}
                    </Button>
                    
                    {/* Grouped "Selected" buttons */}
                    <Box 
                        sx={{ 
                            position: 'relative',
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            paddingTop: '16px'
                        }}
                    >
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                position: 'absolute',
                                top: '-10px',
                                left: '8px',
                                backgroundColor: 'white',
                                padding: '0 4px',
                                color: 'rgba(0, 0, 0, 0.6)',
                                fontSize: '0.75rem'
                            }}
                        >
                            Selected
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button 
                                variant='outlined' 
                                size="small"
                                onClick={() => setDialogProps(getDeleteConfirmationDialog())}
                                disabled={disableAll() || selectedFiles.length === 0}
                            >
                                Delete
                            </Button>
                            <Button 
                                variant='outlined' 
                                size="small"
                                onClick={() => handleIngestOnClick("selected")}
                                disabled={disableAll() || !tenantConfig || selectedFiles.length === 0}
                            >
                                Ingest
                            </Button>
                            {/* prepare offline
                            <Button
                                variant='outlined'
                                size="small"
                                onClick={() => setDialogProps(getPrepareOfflineConfirmationDialog())}
                                disabled={disableAll() || selectedFiles.length === 0}
                            >
                                Prepare Offline
                            </Button>
                            */}
                        </Stack>
                    </Box>

                    <Button variant='outlined'  onClick={() => handleIngestOnClick("new")} disabled={disableAll() || !tenantConfig}>Ingest New</Button>
                    <Button variant='outlined'  onClick={() => handleIngestOnClick("new&failed")} disabled={disableAll() || !tenantConfig}>Ingest New & Failed</Button>

                    {/* Show selected count */}
                    {selectedFiles.length > 0 && !deleteProgress && (
                        <Chip 
                            label={`${selectedFiles.length} selected`} 
                            size="small" 
                            variant="outlined"
                            color="success"
                        />
                    )}
                    
                    {/* Show delete progress */}
                    {deleteProgress && (
                        <Chip 
                            label={`Deleting... ${deleteProgress.deleting.toLocaleString()} of ${deleteProgress.total.toLocaleString()} files`} 
                            size="small" 
                            color="warning" 
                            variant="outlined"
                        />
                    )}
                    
                    {/* Show refresh status */}
                    {isRefreshing && (
                        <Chip 
                            label={`Loading... ${loadingProgress.loaded} files`} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                        />
                    )}

                    {/* Show csv loading */}
                    {csvLoading && (
                        <Chip 
                            label={`Loading CSV selection...`} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                        />
                    )}
                </Stack>
                <Stack direction={'row'} spacing={1}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            refreshFiles()
                        }} 
                        disabled={disableAll()}
                        startIcon={<RefreshIcon/>}
                    >
                        Reload Documents
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                            setInProgress(true)
                            try {
                                await onRefreshStatus()
                            } catch (error) {
                                console.error('Status refresh failed:', error)
                            } finally {
                                setInProgress(false)
                            }
                        }} 
                        disabled={disableAll()}
                        startIcon={<RefreshIcon/>}
                    >
                        Refresh Status
                    </Button>
                </Stack>
            </Stack>
            </div>
            {filteredUploadProgress && <div style={{overflowY: 'scroll', height: 'calc(100% - 130px)',  marginTop: 10, maxHeight: '65vh'}}>
                {filteredUploadProgress.length === 0 && <LinearProgress variant="indeterminate"></LinearProgress>}
                {filteredUploadProgress.map(up => up.isUploading &&
                    <div key={up.uploadedFile} style={{ display: 'flex', flexDirection: 'column' }}>
                        <FormControlLabel disabled value={''} control={<Radio />} label={up.uploadedFile} />
                        {up.position && <LinearProgress variant="determinate" value={normalize(up.position, up)} />}
                    </div>)}
            </div>}
            {!uploadProgress && (
            <div className={classes.documentsGridOuter}>
                {files && <DataGridPro
                    rows={files}
                    columns={columns}
                    getRowId={(row) => row.id}
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 25, page: 0 } },
                    }}
                    checkboxSelection
                    disableRowSelectionOnClick
                    isRowSelectable={(params) => !unselectableStatus.includes(params.row.status) && !disableAll()}
                    rowSelectionModel={selectedFilesIds}
                    onRowSelectionModelChange={(model) => setGridSelectedFile(model)}
                    onStateChange={handleGridStateChange}
                    loading={disableAll()}
                    autoHeight={false}
                    disableColumnMenu={false}
                    disableColumnFilter={false}
                    disableColumnSelector={false}
                    disableDensitySelector={false}
                    slots={{
                        footer: () => (
                            <GridFooterContainer
                                sx={{
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    minHeight: 'auto !important',
                                    py: 1,
                                    px: 1.5,
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    flexWrap="wrap"
                                    useFlexGap
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ flex: 1, minWidth: 0 }}
                                >
                                    <Typography component="span" variant="body2" sx={{ color: '#696969' }}>
                                        pending: {pending}
                                    </Typography>
                                    <Typography component="span" variant="body2" sx={{ color: '#4682B4' }}>
                                        processing: {processing}
                                    </Typography>
                                    <Typography component="span" variant="body2" sx={{ color: '#e57373' }}>
                                        failed: {failed}
                                    </Typography>
                                    <Typography component="span" variant="body2" sx={{ color: '#5cb85c' }}>
                                        done: {done}
                                    </Typography>
                                    {pendingDeletion > 0 && (
                                        <Typography component="span" variant="body2" sx={{ color: '#ef6c00' }}>
                                            pending deletion: {pendingDeletion}
                                        </Typography>
                                    )}
                                    {filesLoading && (
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            sx={{ color: '#ff9800', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            <RefreshIcon sx={{ fontSize: 16 }} />
                                            loading {loadingProgress.loaded} files...
                                        </Typography>
                                    )}
                                </Stack>
                                <Box sx={{ flexShrink: 0, ml: 'auto' }}>
                                    <GridPagination />
                                </Box>
                            </GridFooterContainer>
                        ),
                    }}
                    className={classes.dataGrid}
                />}
            </div>
            )}
            {dialogProps && <StepConfirmation
                confirmationDialogs={[dialogProps]}
                finish={() => { }}
                cancel={handleCancelIngestOnClick}
                isValid={true}
            />}
        </div>
    );
})